import { AxiosResponse } from "axios";
import { PayloadAction } from "@reduxjs/toolkit";
import {
  call,
  CallEffect,
  ForkEffect,
  put,
  PutEffect,
  takeLatest,
} from "redux-saga/effects";
import { ApiResponse, ApiResponseResult } from "middleware/api";
import { actions } from "reducers/slotDetailModal";
import { actions as notificationActions } from "../reducers/notification";
import { actions as slotsActions } from "../reducers/slots";
import { actions as sessionActions } from "../reducers/session";
import * as slotsMiddleware from "middleware/slots";
import {
  SlotDetailResponse,
  SlotDetailType,
  SlotDetailUpdateParams,
  SlotDetailUpdateResponse,
  PickersSlotRequestType,
  PickersSlotResponseType,
  UserSlotRequestType,
  UserSlotType,
  UsersSlotResponseType,
} from "./types/slotDetailModal";
import i18next from "config/i18next";
import { NotificationStateType } from "reducers/types/notification";
import { SlotsFilterType } from "reducers/types/slots";
import { store } from "store";
import { SlotContentResponseType } from "./types/slots";
import { SlotsConstants } from "constants/slots";
import { serverErrorAlert } from "utils/alerts";

const sagas: ForkEffect<never>[] = [
  takeLatest(actions.getPickersFetch.type, getPickers),
  takeLatest(actions.validatePicker.type, validatePicker),
  takeLatest(actions.addNewPickerRequest.type, addNewPicker),
  takeLatest(actions.deletePickerRequest.type, deletePicker),
  takeLatest(actions.getSlotDetailRequest.type, getSlotDetail),
  takeLatest(actions.updateSlotDetailRequest.type, updateSlot),
  takeLatest(actions.deleteSlotRequest.type, deleteSlot),
];

export default sagas;

function* refreshModal() {
  const { slot, dropdownState } = store.getState().slotDetail;
  if (!slot?.id) return;
  yield put(actions.getSlotDetailRequest(slot.id));
  if (dropdownState === "closed") return;
  yield put(
    actions.getPickersFetch({
      slotId: slot.id,
      connected: dropdownState === "connected",
    })
  );
}

function* getPickers({
  payload,
}: PayloadAction<PickersSlotRequestType>): Generator<
  | PutEffect<{ payload: number; type: string }>
  | PutEffect<{ payload: PickersSlotResponseType; type: string }>
  | CallEffect<AxiosResponse<ApiResponseResult<UsersSlotResponseType>>>,
  void,
  ApiResponse<ApiResponseResult<UsersSlotResponseType>>
> {
  const response = yield call(slotsMiddleware.getPickers, payload);
  if (response.status !== 200) {
    yield put(actions.getPickersError(response.status));
  } else {
    const { result } = response.data;
    yield put(
      actions.getPickersSuccess({
        connected: payload.connected,
        users: result.items,
      })
    );
  }
}

function* validatePicker({
  payload,
}: PayloadAction<UserSlotRequestType>): Generator<
  | PutEffect<{ payload: number; type: string }>
  | PutEffect<{ payload: UserSlotType; type: string }>
  | ReturnType<typeof refreshModal>
  | CallEffect<AxiosResponse<ApiResponseResult<UserSlotType>>>,
  void,
  ApiResponse<ApiResponseResult<UserSlotType>>
> {
  const { slotId, pickerId } = payload;
  const { status, data } = yield call(
    slotsMiddleware.validatePicker,
    slotId,
    pickerId
  );
  if (status !== 200) {
    if (SlotsConstants.isInvalidSlotStatus(data?.statusCode || 0)) {
      yield refreshModal();
    } else {
      const statusCode =
        data?.statusCode === 404
          ? SlotsConstants.DISABLED_PICKER_ERROR
          : data?.statusCode;
      yield put(actions.validatePickerInvalid(statusCode || 0));
    }
  } else {
    const { result } = data;
    yield put(actions.validatePickerValid(result));
  }
}

function* addNewPicker({
  payload,
}: PayloadAction<UserSlotRequestType>): Generator<
  | PutEffect<{ payload: undefined; type: string }>
  | PutEffect<{ payload: number; type: string }>
  | PutEffect<{ payload: boolean; type: string }>
  | ReturnType<typeof refreshModal>
  | CallEffect<AxiosResponse<ApiResponseResult>>,
  void,
  ApiResponse<ApiResponseResult>
> {
  const { status, data } = yield call(slotsMiddleware.addNewPicker, payload);
  if (status < 200 || status >= 300) {
    if (SlotsConstants.isInvalidSlotStatus(data?.statusCode || 0)) {
      yield refreshModal();
      yield put(actions.resetNewPicker());
    } else {
      const statusCode =
        data?.statusCode === 404
          ? SlotsConstants.DISABLED_PICKER_ERROR
          : data?.statusCode;
      yield put(actions.addNewPickerError(statusCode || 0));
    }
  } else {
    yield put(actions.addNewPickerSuccess());
    yield put(actions.setPickerDirty(false));
    yield refreshModal();
  }
}

function* deletePicker({
  payload,
}: PayloadAction<UserSlotRequestType>): Generator<
  | PutEffect<{ payload: undefined; type: string }>
  | PutEffect<{ payload: NotificationStateType; type: string }>
  | ReturnType<typeof refreshModal>
  | CallEffect<AxiosResponse<null>>,
  void,
  ApiResponse<null>
> {
  const response = yield call(slotsMiddleware.deletePicker, payload);
  if (response.status !== 204) {
    yield refreshModal();
    yield put(actions.deletePickerError());

    yield put(
      notificationActions.showNotification({
        id: "notification-error-picker",
        level: "error",
        title: i18next.t("slotDetail:deletionNotAllowedTitle"),
        body: i18next.t("slotDetail:deletionNotAllowedMessage"),
      })
    );
  } else {
    yield put(actions.deletePickerSuccess());
    yield refreshModal();
  }
}

function* getSlotDetail({
  payload,
}: PayloadAction<number>): Generator<
  CallEffect<AxiosResponse<SlotDetailType>> | PutEffect<{ type: string }>,
  void,
  AxiosResponse<SlotDetailResponse>
> {
  const response = yield call(slotsMiddleware.getSlotDetail, payload);
  if (response.status !== 200) {
    yield put(actions.getSlotDetailError());
    yield put(notificationActions.showNotification(serverErrorAlert()));
  } else {
    const { result } = response.data;
    yield put(actions.getSlotDetailSuccess(result));
  }
}

function* updateSlot({
  payload,
}: PayloadAction<SlotDetailUpdateParams>): Generator<
  | CallEffect<AxiosResponse<SlotDetailUpdateResponse>>
  | PutEffect<{ type: string }>
  | void,
  void,
  ApiResponse<SlotDetailUpdateResponse>
> {
  const response = yield call(
    slotsMiddleware.updateSlot,
    payload.body,
    payload.regionId,
    payload.id
  );
  if (response.status !== 200) {
    yield put(actions.updateSlotDetailError());
    switch (response.data?.statusCode) {
      case 50006:
        yield put(
          notificationActions.showNotification({
            id: "notification-slot-creation-error",
            level: "error",
            title: i18next.t("slotCreation:invalidTitle"),
            body: i18next.t("slotCreation:invalidMessage"),
          })
        );
        break;
      default:
        yield put(
          notificationActions.showNotification({
            id: "notification-slot-update-error",
            level: "error",
            title: i18next.t("slotDetail:editSlotErrorTitle"),
            body: i18next.t("slotDetail:editSlotErrorMessage"),
            onClose: payload.refresh,
          })
        );
    }
  } else {
    yield put(actions.updateSlotDetailSuccess());
    const { filters, filtersExtra } = store.getState().slots;
    yield put(
      notificationActions.showNotification({
        id: "notification-slot-update-success",
        level: "success",
        title: i18next.t("slotDetail:successTitle"),
        body: i18next.t("slotDetail:successMessage"),
        onClose: () => {
          store.dispatch(slotsActions.toggleDetailModalVisibility(false));
          store.dispatch(
            slotsActions.getSlotsRequest({
              ...filters,
              ...filtersExtra,
            })
          );
          store.dispatch(sessionActions.setDirty(false));
        },
      })
    );
  }
}
function* deleteSlot({
  payload,
}: PayloadAction<number>): Generator<
  | PutEffect<{ payload: number; type: string }>
  | PutEffect<{ payload: NotificationStateType; type: string }>
  | PutEffect<{ payload: undefined; type: string }>
  | PutEffect<{ payload: SlotsFilterType; type: string }>
  | PutEffect<{ payload: boolean; type: string }>
  | void
  | CallEffect<unknown>,
  void,
  ApiResponse<SlotContentResponseType>
> {
  const response = yield call(slotsMiddleware.deleteSlot, payload);
  if (response.status !== 200) {
    switch (response.data?.statusCode) {
      case 404:
      case SlotsConstants.CANCELLED_SLOT_ERROR:
      case SlotsConstants.EXPIRED_SLOT_ERROR:
        yield put(
          notificationActions.showNotification({
            id: "notification-delete-error",
            level: "error",
            title: i18next.t("slotDetail:deleteErrorTitle"),
            body: i18next.t("slotDetail:deleteErrorMessage"),
          })
        );
        break;
    }
    yield put(actions.deleteSlotError());
  } else {
    const { filters, filtersExtra } = store.getState().slots;
    yield put(slotsActions.getSlotsRequest({ ...filters, ...filtersExtra }));
    yield put(actions.deleteSlotSuccess());
    yield put(slotsActions.toggleDetailModalVisibility(false));
  }
}
